import {
  ALERT_SEVERITY,
  ALERT_STYLE,
  ALERT_STYLE_DESCRIPTION,
  INCOME_RULE_ID,
  QUESTION_ID,
  RULE_CATEGORY_ID,
  RULE_ID,
  RULE_REFERENCE_TYPE_ID,
  RULE_RESULT_STATUS_ID,
  RULE_SUB_CATEGORY_ID,
} from './api-ids';
import API_STATUS from './api-status';
import REACT_QUERY_STATUS from './react-query-status';
import { US_STATES, US_STATES_LABEL_VALUE_ARRAY } from './us-states';

export const TRUE_STRING_VALUE = 'true';
export const FALSE_STRING_VALUE = 'false';

const ALERT_TYPES = {
  CHILD: 'Child',
  COMPLETED: 'Completed',
  PARENT: 'Parent',
  VERIFIED: 'Child',
  REVIEW: 'Review',
} as const;

const USER_ROLE = {
  SR_UNDERWRITER: 'SR Underwriter',
  UNDERWRITER: 'Underwriter',
  ACCOUNT_EXECUTIVE: 'AccountExecutive',
  BROKER: 'Broker',
  CLOSER: 'Closing',
  CLOSER_TL: 'Closing Team Lead',
  TRAC_SUPPORT: 'Trac Support',
  TRAC_ESCALATIONS: 'Trac Escalations',
} as const;
Object.freeze(USER_ROLE);

const UNDERWRITER_ROLES = [USER_ROLE.SR_UNDERWRITER, USER_ROLE.UNDERWRITER];

export const BOLT_IMPERSONATE_COOKIE = (
  obfuscatedLoanRecordId: string | number,
) => 'boltimpersonate' + `${obfuscatedLoanRecordId}`;

export const YES = 'Yes';
export const NO = 'No';

const SIGNAL_R_STATUS = {
  RULE_EVALUATION_COMPLETED: 0,
  ADR_PROCESS_COMPLETED: 1,
  CLASSIFY_STATUS_CHANGE: 2,
  ADR_PROCESS_FAILED: 3,
  FRAUD_GUARD_SUCCESS: 4,
  AUS_COMPLETED: 5,
  THIRD_PARTY_CHECK_ALERT: 6,
  BOLT_CONDITIONS_APPLIED: 7,
  RULE_EVALUATION_ERROR: 8,
  AUS_STARTED: 9,
  AUS_RECOMMENDATION_ACCEPTABLE: 10,
  AUS_RECOMMENDATION_NOT_ACCEPTABLE: 11,
  REQUEST_COMPLETED: 14,
  REQUEST_ERRORED: 15,
  LOAN_STATUS_FLIP_SUCCESS: 18,
  LOAN_STATUS_FLIP_FAILED: 19,
  ADE_PROCESS_COMPLETED: 20,
  AUS_ERROR: 21,
  AUS_SUCCESS: 22,
  AUS_FAILED: 23,
  AUTO_COFC_COMPLETE: 24,
  AUTO_COFC_INCOMPLETE: 25,
  AUS_PROCESS_DATA_FAILED: 26,
} as const;

const AUS_METHOD = {
  1: 'DU',
  2: 'LPA',
} as const;

const RADIO_BUTTON_OPTIONS = ['Yes', 'No'];

const GIFT_OF_EQUITY_OPTIONS = {
  APPRAISAL_WAIVER: 'AppraisalWaiver',
  GIFT_OF_EQUITY: 'GiftOfEquity',
} as const;

const CREDIT = {
  LOAN_PRODUCT_ADVISOR: 'Loan Product Advisor',
  DESKTOP_UNDERWRITER: 'Desktop Underwriter',
} as const;

const FORM_MESSAGES = {
  required: 'Required',
  requiredField: 'Required field',
  selectAnOption: 'Select an option',
  startDate: (startDate) =>
    'Date entered cannot be before start date of ' + startDate,
  currentDate:
    'Date entered cannot be after the current date of ' +
    new Date().toLocaleDateString(),
  maxLength: 'Character limit reached.',
  minValue: (value) => `Cannot be less than ${value}`,
  maxValue: (value) => `Cannot be more than ${value}`,
};

const RESOLUTION_TYPE_ID = {
  NEW_CONDITION_ACTION_REQUIRED: 1,
  CONDITIONS_ADDED_TO_LOAN: 2,
  NO_ACTION: 3,
  NEXT_QUESTION: 4,
} as const;
Object.freeze(RESOLUTION_TYPE_ID);

const RESOLUTION_TYPE_ALERT_MAPPING = {
  [RESOLUTION_TYPE_ID.NEW_CONDITION_ACTION_REQUIRED]: 'warning',
  [RESOLUTION_TYPE_ID.CONDITIONS_ADDED_TO_LOAN]: 'warning',
  [RESOLUTION_TYPE_ID.NO_ACTION]: 'success',
} as const;
Object.freeze(RESOLUTION_TYPE_ALERT_MAPPING);

const REGEX = {
  HANDLEBAR: /{{.*?}}/, // capture everything within the {{}}
  SPECIAL_CHARACTERS: /[^a-zA-Z0-9' ]+/g, // captures non-alphanumerics
};

const DRY_STATES = ['AK', 'AZ', 'CA', 'HI', 'ID', 'NV', 'OR', 'WA'];

const LOAN_PRODUCT_TYPE = {
  CONVENTIONAL: 1,
  VA: 2,
  FHA: 3,
  USDA: 4,
} as const;

const LOAN_PRODUCT_TYPE_BY_NUMBER = {
  1: 'Conventional',
  2: 'VA',
  3: 'FHA',
  4: 'USDA',
} as const;

const LOAN_PRODUCT_TYPE_BY_NAME = {
  FHA: 'FHA',
  CONVENTIONAL: 'Conventional',
  VA: 'VA',
  USDA: 'USDA',
} as const;

const OCCUPANCY_TYPE_BY_NAME = {
  PRIMARY_RESIDENCE: 1,
  INVESTMENT_PROPERTY: 2,
  SECONDARY_RESIDENCE: 3,
  RESIDENCE_BEYOND_SECOND_HOME: 4,
  OWNER_OCCUPIED: 5,
  INVESTMENT_OCCUPY_FOURTEEN_DAYS: 6,
} as const;

const PROPERTY_TYPES = {
  SFR: 'SFR',
  MULTIUNIT: '2-4Unit',
  CONDO: 'Condo',
} as const;

const LOAN_PURPOSE_TYPE = {
  REFI_FIRST_MORTGAGE: 3,
  PURCHASE: 1,
  HELOC: 2,
  CONSTRUCTION_TO_PERMANENT: 6,
  PURCHASE_SECOND_MORTGAGE: 9,
  REFINANCE_SECOND_MORTGAGE: 10,
  HELOC_SECOND_MORTGAGE: 11,
  CONSTRUCTION_REFINANCE: 12,
} as const;

const BUSINESS_CHANNEL = {
  WHOLESALE: 0,
  CONSUMER_DIRECT: 1,
  RETAIL: 2,
  EMPLOYEE: 3,
  CORRESPONDENT: 4,
} as const;

const INVESTOR_COLLATERAL_PROGRAM_IDENTIFIER = {
  BLANK: 0,
  PIW: 1,
  ID2075: 2,
  LEVEL1_PIW: 3,
  ACE: 4,
  EASY_VALUATION: 6,
  FAST_FREDDIE: 7,
} as const;

export const PROPERTY_REVIEW_LEVEL_BY_NUMBER = {
  15: 'Drive By',
  16: 'Full Appraisal',
  17: 'None',
  27: 'Field Review',
} as const;

export const LOAN_PURPOSE_TYPE_BY_NUMBER = {
  0: '',
  1: 'Purchase Home',
  2: 'HELOC',
  3: 'Refinance 1st Mortgage',
  6: 'Construction to permanent',
  9: 'Purchase second mortgage',
  10: 'Refinance second mortgage',
  11: 'HELOC second mortgage',
  12: 'Construction Refinance',
} as const;

export const LOAN_PURPOSE_TYPE_BY_NAME = {
  PURCHASE_HOME: 'Purchase Home',
  HELOC: 'HELOC',
  REFINANCE_FIRST_MORTGAGE: 'Refinance 1st Mortgage',
  CONSTRUCTION_TO_PERMANENT: 'Construction to permanent',
  PURCHASE_SECOND_MORTGAGE: 'Purchase second mortgage',
  REFINANCE_SECOND_MORTGAGE: 'Refinance second mortgage',
  HELOC_SECOND_MORTGAGE: 'HELOC second mortgage',
  CONSTRUCTION_REFINANCE: 'Construction Refinance',
} as const;

export const REFI_PURPOSE = {
  CASH_OUT: 'Cash Out',
  CASH_OUT_COLLEGE: 'Cash Out / College Education',
  CASH_OUT_OTHER: 'Cash Out / Other',
  CASH_OUT_FHA: 'Cash Out-FHA',
  CASH_OUT_HOME_IMPROVEMENT: 'Cash Out / Home Improvement',
  CASH_OUT_COLLEGE_EDUCATION: 'Cash Out / College Education',
  CASH_OUT_STUDENT_LOAN: 'Cash Out / Student Loan',
  CASH_OUT_DEBT_CONSOLIDATION: 'Debt Consolidation',
  VA_CASH_OUT_TYPE_I: 'VA Cash Out - Type I',
  VA_CASH_OUT_TYPE_II: 'VA Cash Out - Type II',
  VA_PATRIOT_PLUS_PROGRAM: 'VA Patriot Plus Program',
  RATE_AND_TERM_REDUCTION_FHA: 'Rate And Term Reduction-FHA',
  RATE_AND_TERM_REDUCTION_CONVENTIONAL: 'Rate And Term Reduction-CONV',
  RATE_AND_TERM_EXTENDED: 'Expanded Rate And Term',
  VA_IRRRL: 'VA Interest Rate Reduction',
  STREAMLINE_REFINANCE: 'Streamline Refinance',
  NOT_APPLICABLE: 'Not Applicable',
} as const;

export const AUS_METHOD_BY_NUMBER = {
  1: 'Desktop Underwriter',
  2: 'Loan Product Advisor',
  3: 'Manual Underwriting',
} as const;

export const PROPERTY_TYPES_BY_NUMBER = {
  0: 'Unknown',
  22: 'Single Family Residence',
  23: 'Condotels',
  25: '2-4 Unit Dwelling',
  26: 'Mobile Home',
  27: 'Manufactured Housing',
  28: 'Panelized',
  30: 'Cooperative',
  31: 'Multi-Use Property',
  32: 'Planned Unit Development',
  34: 'Condominium',
  35: 'High Rise Condo',
  36: 'Low Rise Condo',
  37: 'Mixed-Use',
  38: 'Modular',
  39: 'Mid Rise Condo',
  40: 'Prefab',
  41: 'Warrantable Condominium',
  42: 'Non-Warrantable Condominium',
  43: 'Mfr Purchase Home/Refi Land',
  44: 'Modular Purchase Home/Refi Land',
  45: 'Manufactured Condo',
  46: 'Manufactured PUD',
  47: 'SFR Purchase Home/Refi Land',
  48: 'Site Condo',
  49: 'PUD Purchase Home/Refi Land',
  50: 'Vacant Land',
  51: 'Commercial',
  52: 'Manufactured Condo Single Wide',
  53: 'Manufactured PUD Single Wide',
  54: 'Manufactured Single Wide',
  55: 'Manufactured Multi Wide',
  56: 'Manufactured Condo Multi Wide',
  57: 'Manufactured PUD Multi Wide',
} as const;

export const PROPERTY_TYPE_DESCRIPTIONS = {
  UNKNOWN: 'Unknown',
  SINGLE_FAMILY_RESIDENCE: 'Single Family Residence',
  CONDOTELS: 'Condotels',
  TWO_TO_FOUR_UNIT_DWELLING: '2-4 Unit Dwelling',
  MOBILE_HOME: 'Mobile Home',
  MANUFACTURED_HOUSING: 'Manufactured Housing',
  PANELIZED: 'Panelized',
  COOPERATIVE: 'Cooperative',
  MULTI_USE_PROPERTY: 'Multi-Use Property',
  PLANNED_UNIT_DEVELOPMENT: 'Planned Unit Development',
  CONDOMINIUM: 'Condominium',
  HIGH_RISE_CONDO: 'High Rise Condo',
  LOW_RISE_CONDO: 'Low Rise Condo',
  MIXED_USE: 'Mixed-Use',
  MODULAR: 'Modular',
  MID_RISE_CONDO: 'Mid Rise Condo',
  PREFAB: 'Prefab',
  WARRANTABLE_CONDOMINIUM: 'Warrantable Condominium',
  NON_WARRANTABLE_CONDOMINIUM: 'Non-Warrantable Condominium',
  MFR_PURCHASE_HOME_REFI_LAND: 'Mfr Purchase Home/Refi Land',
  MODULAR_PURCHASE_HOME_REFI_LAND: 'Modular Purchase Home/Refi Land',
  MANUFACTURED_CONDO: 'Manufactured Condo',
  MANUFACTURED_PUD: 'Manufactured PUD',
  SFR_PURCHASE_HOME_REFI_LAND: 'SFR Purchase Home/Refi Land',
  SITE_CONDO: 'Site Condo',
  PUD_PURCHASE_HOME_REFI_LAND: 'PUD Purchase Home/Refi Land',
  VACANT_LAND: 'Vacant Land',
  COMMERCIAL: 'Commercial',
  MANUFACTURED_CONDO_SINGLE_WIDE: 'Manufactured Condo Single Wide',
  MANUFACTURED_PUD_SINGLE_WIDE: 'Manufactured PUD Single Wide',
  MANUFACTURED_SINGLE_WIDE: 'Manufactured Single Wide',
  MANUFACTURED_MULTI_WIDE: 'Manufactured Multi Wide',
  MANUFACTURED_CONDO_MULTI_WIDE: 'Manufactured Condo Multi Wide',
  MANUFACTURED_PUD_MULTI_WIDE: 'Manufactured PUD Multi Wide',
} as const;

export const OCCUPANCY_TYPE_BY_NUMBER = {
  1: 'Primary Residence',
  2: 'Investment Property',
  3: 'Second Home',
  4: 'Residence Beyond 2nd Home',
  5: 'Owner Occupied',
  6: 'Investment Occupy Fourteen Days',
} as const;

export const ATTACHED_PROPERTY_TYPE_BY_NUMBER = {
  0: 'Detached',
  1: 'Attached',
  2: 'Semi-Detached',
  99: 'Blank',
  [-1]: 'Unknown',
} as const;

const SUB_CATEGORY_IDS = {
  CREDIT: 1,
  CREDIT_LIABILITIES: 3,
  NET_TANGIBLE_BENEFITS: 5,
  INCOME_CALCULATOR: 6,
  BORROWER1003: 7,
  PURCHASE_AGREEMENT: 8,
  FHA_REQUIREMENTS: 17,
  ASSETS: 9,
  REO: 10,
  THIRD_PARTY_CHECKS_: 11,
  RENTAL_INCOME: 12,
  FINAL_CHECKS: 13,
  HOMEOWNERS_INSURANCE: 14,
  EARLY_CLOSING_DISCLOSURE: 15,
  MANUAL_CHECKS: 16,
  SUBJECT_PROPERTY: 19,
  NON_OCCUPYING_CO_BORROWERS: 18,
  FINAL_UNDERWRITE_REVIEW: 20,
  TITLE: 21,
  TAXES: 22,
  SUPPLIMENTAL_INSURANCE: 23,
  FLOOD_INSURANCE: 24,
  UNDERWRITER_FHA: 25,
  VA_BORROWER: 26,
  VA_LIABILITIES: 27,
  VA: 28,
  APPRAISAL: 29,
  NEW_CONSTRUCTION_REQUIREMENTS: 30,
} as const;

export const TRACK_TYPE_DESCRIPTIONS = {
  Traditional: "Traditional Lender's Policy",
  Trac: 'TRAC',
  TracPlus: 'TRAC+',
  Doma: 'TRAC Lite',
};

export const LOAN_SUMMARY_STATUS_BY_NAME = {
  REJECTED: 33,
  FILE_IMPORTED: 47,
  UNDERWRITING: 49,
  WHOLESALE_SUSPENDED: 50,
  APPROVED_WITH_CONDITIONS: 51,
  WHOLESALE_CLEAR_TO_CLOSE: 52,
  WHOLESALE_CLOSING: 53,
  POST_CLOSED: 54,
  QUALITY_CONTROL_HOLD: 56,
  UNSALEABLE_LOANS: 57,
  REQUEST_FOR_DISBURSEMENT: 58,
  POST_CLOSE_PROBLEM: 93,
};

export const DOCUMENT_TYPE_KEYS = {
  PRE_CLOSING_STATUS_REPORT: 'PRE-CLOSINGSTATUSREPORT-TRACLITE',
  PRELIMINARY_TITLE_COMMITMENT: 'PreliminaryTitleCommitment',
  TAX_INFORMATION: 'TAXINFORMATION-0',
  TAX_INFORMATION_CORE_LOGIC: 'TAXINFORMATIONCORELOGIC',
  TITLE_SUMMARY: 'TITLESUMMARY',
};

export {
  ALERT_SEVERITY,
  ALERT_STYLE,
  ALERT_STYLE_DESCRIPTION,
  ALERT_TYPES,
  API_STATUS,
  REACT_QUERY_STATUS,
  CREDIT,
  FORM_MESSAGES,
  INCOME_RULE_ID,
  DRY_STATES,
  QUESTION_ID,
  RADIO_BUTTON_OPTIONS,
  REGEX,
  RESOLUTION_TYPE_ID,
  RESOLUTION_TYPE_ALERT_MAPPING,
  RULE_CATEGORY_ID,
  RULE_ID,
  RULE_REFERENCE_TYPE_ID,
  RULE_RESULT_STATUS_ID,
  RULE_SUB_CATEGORY_ID,
  SIGNAL_R_STATUS,
  SUB_CATEGORY_IDS,
  UNDERWRITER_ROLES,
  USER_ROLE,
  US_STATES,
  US_STATES_LABEL_VALUE_ARRAY,
  LOAN_PRODUCT_TYPE,
  LOAN_PRODUCT_TYPE_BY_NUMBER,
  LOAN_PRODUCT_TYPE_BY_NAME,
  PROPERTY_TYPES,
  LOAN_PURPOSE_TYPE,
  GIFT_OF_EQUITY_OPTIONS,
  OCCUPANCY_TYPE_BY_NAME,
  INVESTOR_COLLATERAL_PROGRAM_IDENTIFIER,
  BUSINESS_CHANNEL,
  AUS_METHOD,
};

export const INCOME_RECONCILIATION = {
  IncomeReconciliation: 'Income Reconciliation',
  RealEstateOwned: 'REO',
  SubjectProperty: 'Subject Property',
};

export const INSURANCE_DOCUMENT_TYPES = {
  homeownersInsurance: 'Property Insurance Policy',
  supplementalInsurance: 'Supplemental Insurance Policy',
};

export const BUSINESS_CHANNEL_ID_BY_NAME = {
  correspondent: 5,
};

export const HOTKEY_SHORTCUTS = {
  KeyH: { type: 'bolt', slug: 'classify' }, // ALT+Shift+H
  KeyJ: { type: 'bolt', slug: 'borrower' }, // ALT+Shift+J
  KeyK: { type: 'bolt', slug: 'liabilities' }, // ALT+Shift+K
  KeyL: { type: 'bolt', slug: 'income' }, // ALT+Shift+L
  Quote: { type: 'bolt', slug: 'assets' }, // ALT+Shift+'
  Semicolon: { type: 'bolt', slug: 'property' }, // ALT+Shift+;
  KeyN: { type: 'bolt', slug: 'fha' }, // ALT+Shift+N
  KeyV: { type: 'bolt', slug: 'va' }, // ALT+Shift+V
  Comma: { type: 'bolt', slug: 'final-review' }, // ALT+Shift+,
  KeyM: { type: 'bolt', slug: 'status-and-review' }, // ALT+Shift+M

  KeyY: { type: 'info', key: 'borrower' }, // ALT+Shift+Y
  KeyU: { type: 'info', key: 'liabilities' }, // ALT+Shift+U
  KeyI: { type: 'info', key: 'income' }, // ALT+Shift+I
  KeyO: { type: 'info', key: 'assets' }, // ALT+Shift+O
  KeyP: { type: 'info', key: 'reo' }, // ALT+Shift+P
  // Add more as needed
};

export const SNAPSHOT_BUTTON_LABEL = 'Show snapshot context in PDF viewer';
